<template>
  <section class="results-center">
    <header>
      <div>
        <span class="eyebrow">JIANGK · CLASSIFICATION</span>
        <h2>两航道换道成绩</h2>
        <p>选择模式后即时汇总，只读取已发布成绩：两阶段累计=同一队伍两阶段有效时间相加，按总用时从少到多；单阶段暂列=可添加多个阶段组，同一队伍取最快有效时间升序，用于第二轮未开始时的整体评估。逐组发布自动更新，更正或撤回后同步重算。</p>
      </div>
      <a-button icon="reload" :loading="loading" @click="loadRows">刷新</a-button>
    </header>
    <a-form layout="vertical" class="rule-grid">
      <a-form-item label="模式">
        <a-radio-group v-model="mode" :disabled="saving" button-style="solid">
          <a-radio-button value="two_stage_sum">两阶段累计</a-radio-button>
          <a-radio-button value="single_stage_rank">单阶段暂列</a-radio-button>
        </a-radio-group>
      </a-form-item>
      <a-form-item v-if="mode === 'two_stage_sum'" label="第一阶段（可添加多个组，同队取最快有效时间）">
        <div class="stage-add">
          <a-tag v-for="(name, index) in stage1Stages" :key="name" closable :disable-transitions="true" @close="removeStage('stage1', index)">{{ stageLabel(name) }}</a-tag>
          <a-select :value="undefined" style="width: 240px;" placeholder="添加阶段" :disabled="saving" @change="value => addStage('stage1', value)">
            <a-select-option v-for="item in stageOptions" :key="item.value" :value="item.value" :disabled="stage1Stages.includes(item.value) || stage2Stages.includes(item.value)">{{ item.label }} · {{ item.count }}场</a-select-option>
          </a-select>
        </div>
      </a-form-item>
      <a-form-item v-else label="阶段（可添加多个组，同队取最快有效时间）">
        <div class="stage-add">
          <a-tag v-for="(name, index) in singleStages" :key="name" closable :disable-transitions="true" @close="removeStage('single', index)">{{ stageLabel(name) }}</a-tag>
          <a-select :value="undefined" style="width: 240px;" placeholder="添加阶段" :disabled="saving" @change="value => addStage('single', value)">
            <a-select-option v-for="item in stageOptions" :key="item.value" :value="item.value" :disabled="singleStages.includes(item.value)">{{ item.label }} · {{ item.count }}场</a-select-option>
          </a-select>
        </div>
      </a-form-item>
      <a-form-item v-if="mode === 'two_stage_sum'" label="第二阶段（可添加多个组，同队取最快有效时间）">
        <div class="stage-add">
          <a-tag v-for="(name, index) in stage2Stages" :key="name" closable :disable-transitions="true" @close="removeStage('stage2', index)">{{ stageLabel(name) }}</a-tag>
          <a-select :value="undefined" style="width: 240px;" placeholder="添加阶段" :disabled="saving" @change="value => addStage('stage2', value)">
            <a-select-option v-for="item in stageOptions" :key="item.value" :value="item.value" :disabled="stage2Stages.includes(item.value) || stage1Stages.includes(item.value)">{{ item.label }} · {{ item.count }}场</a-select-option>
          </a-select>
        </div>
      </a-form-item>
      <a-form-item>
        <a-button type="primary" :loading="saving" :disabled="!canSave" @click="save">保存并汇总</a-button>
      </a-form-item>
    </a-form>
    <a-alert v-if="configured && rows.length && summary && summary.preliminary" type="info" show-icon message="比赛尚未全部结束，排名为“暂列排名”；每发布一组成绩自动更新。" style="margin-bottom:12px" />
    <a-table :data-source="rows" :columns="columns" row-key="eventEntryId" :pagination="{ pageSize: 30, showSizeChanger: false }" :scroll="{ x: 900 }" size="small" :loading="loading" :locale="{ emptyText: configured ? '暂无参赛队伍' : '请在上方选择并保存阶段' }" />
  </section>
</template>
<script>
import request from '@/utils/request'
import { getDragonScoringRule, saveDragonScoringRule, clearDragonScoringRule } from '@/api'

function formatSummaryTime(millis) {
  const value = Number(millis)
  if (!Number.isSafeInteger(value) || value <= 0) return '—'
  const minutes = Math.floor(value / 60000)
  const seconds = Math.floor(value / 1000) % 60
  const ms = value % 1000
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(ms).padStart(3, '0')}`
}

export default {
  props: { event: { type: Object, required: true }, categories: { type: Array, default: () => [] } },
  data() {
    return {
      rounds: [],
      mode: 'two_stage_sum',
      stage1Stages: [],
      stage2Stages: [],
      singleStages: [],
      configured: false,
      summary: null,
      loading: false,
      saving: false,
      timer: null
    }
  },
  computed: {
    stageOptions() {
      const seen = new Map()
      ;(this.rounds || []).forEach(item => {
        if (!item.roundName) return
        const category = (this.categories || []).find(c => c.id === item.categoryId)
        const value = this.stageValueOf(item)
        if (!seen.has(value)) {
          seen.set(value, {
            value,
            label: (category ? category.categoryName + ' · ' : '') + item.roundName + (item.distance ? '（' + item.distance + '米）' : ''),
            count: 0
          })
        }
        seen.get(value).count++
      })
      return Array.from(seen.values())
    },
    columns() {
      if (this.mode === 'single_stage_rank') {
        return [
          { title: '排名', key: 'rank', width: 110, customRender: (_, row) => row.rankText },
          { title: '队伍', dataIndex: 'teamName', width: 260 },
          { title: '暂列成绩', key: 'first', width: 160, customRender: (_, row) => row.firstText }
        ]
      }
      return [
        { title: '排名', key: 'rank', width: 110, customRender: (_, row) => row.rankText },
        { title: '队伍', dataIndex: 'teamName', width: 260 },
        { title: '第一轮成绩', key: 'first', width: 160, customRender: (_, row) => row.firstText },
        { title: '第二轮成绩', key: 'second', width: 160, customRender: (_, row) => row.secondText },
        { title: '总成绩', key: 'total', width: 160, customRender: (_, row) => row.totalText }
      ]
    },
    rows() {
      const summary = this.summary
      if (!summary || summary.enabled !== true) return []
      return (summary.rows || []).map(item => ({
        ...item,
        firstText: formatSummaryTime(item.firstMillis),
        secondText: item.secondMillis != null ? formatSummaryTime(item.secondMillis) : '—',
        totalText: item.totalMillis != null ? formatSummaryTime(item.totalMillis) : '—',
        rankText: item.rank != null ? item.rank : '—'
      }))
    },
    canSave() {
      return !this.saving
    }
  },
  created() {
    this.load()
    this.timer = setInterval(() => this.loadRows(), 15000)
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    stageValueOf(round) {
      return (round.categoryId == null ? '' : round.categoryId) + '|' + round.roundName + '|' + (round.distance == null ? '' : round.distance)
    },
    stageLabel(value) {
      const option = (this.stageOptions || []).find(item => item.value === value)
      return option ? option.label : value
    },
    stageValuesOf(ids) {
      const values = []
      ;(ids || []).forEach(id => {
        const round = (this.rounds || []).find(item => item.id === id)
        if (!round || !round.roundName) return
        const value = this.stageValueOf(round)
        if (!values.includes(value)) values.push(value)
      })
      return values
    },
    stageList(target) {
      if (target === 'single') return this.singleStages
      return target === 'stage1' ? this.stage1Stages : this.stage2Stages
    },
    addStage(target, value) {
      const list = this.stageList(target)
      if (!value || list.includes(value)) return
      list.push(value)
    },
    removeStage(target, index) {
      this.stageList(target).splice(index, 1)
    },
    addSingleStage(value) {
      this.addStage('single', value)
    },
    removeSingleStage(index) {
      this.removeStage('single', index)
    },
    stageNameOf(ids) {
      if (!ids || !ids.length) return undefined
      const rounds = (this.rounds || []).filter(item => ids.includes(item.id))
      if (rounds.length !== ids.length || !rounds.length) return undefined
      const names = new Set(rounds.map(item => this.stageValueOf(item)))
      return names.size === 1 ? Array.from(names)[0] : undefined
    },
    stagesToRoundIds(value) {
      if (!value) return []
      return (this.rounds || [])
        .filter(item => this.stageValueOf(item) === value)
        .map(item => item.id)
    },
    unionRoundIds(values) {
      return (values || []).reduce((all, value) => all.concat(this.stagesToRoundIds(value)), [])
    },
    async load() {
      this.loading = true
      try {
        const eventId = this.event.id
        const [ruleRes, roundsRes] = await Promise.all([
          getDragonScoringRule({ eventId }),
          request({ url: '/dragonboat/round/options', params: { eventId } })
        ])
        this.rounds = roundsRes.data || []
        const rule = ruleRes.data || {}
        const config = rule.ruleConfig
        if (rule.scoringType === 'two_stage_sum' && config) {
          this.mode = 'two_stage_sum'
          this.stage1Stages = this.stageValuesOf(config.stage1RoundIds || [])
          this.stage2Stages = this.stageValuesOf(config.stage2RoundIds || [])
          this.configured = this.stage1Stages.length > 0 && this.stage2Stages.length > 0
        } else if (rule.scoringType === 'single_stage_rank' && config) {
          this.mode = 'single_stage_rank'
          this.singleStages = this.stageValuesOf(config.stage1RoundIds || [])
          this.configured = this.singleStages.length > 0
        } else {
          this.stage1Stages = []
          this.stage2Stages = []
          this.singleStages = []
          this.configured = false
        }
        await this.loadRows()
      } catch (e) {
        this.$message.error(e.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    async loadRows() {
      if (!this.configured) {
        this.summary = null
        return
      }
      try {
        const res = await request({ url: '/app/result/two-stage-summary', params: { eventId: this.event.id }, silentError: true })
        this.summary = res.data || null
      } catch (e) {
        // Keep the last table; the next refresh or poll retries.
      }
    },
    async save() {
      const single = this.mode === 'single_stage_rank'
      const nothingSelected = single ? !this.singleStages.length : (!this.stage1Stages.length && !this.stage2Stages.length)
      if (nothingSelected) {
        // 主动移除全部选择后保存：清除规则，回到未配置的空白状态。
        if (this.saving) return
        this.saving = true
        try {
          await clearDragonScoringRule({ eventId: this.event.id })
          this.configured = false
          this.summary = null
          this.$message.success('已清除规则，恢复初始状态')
        } catch (e) {
          this.$message.error(e.message || '清除失败')
        } finally {
          this.saving = false
        }
        return
      }
      let stage1RoundIds
      let stage2RoundIds = []
      if (single) {
        stage1RoundIds = this.unionRoundIds(this.singleStages)
      } else {
        if (!this.stage1Stages.length || !this.stage2Stages.length) return this.$message.warning('请为两个阶段各添加至少一个组')
        stage1RoundIds = this.unionRoundIds(this.stage1Stages)
        stage2RoundIds = this.unionRoundIds(this.stage2Stages)
      }
      if (!stage1RoundIds.length || (!single && !stage2RoundIds.length)) return this.$message.warning('所选阶段没有场次，请核对后重选')
      this.saving = true
      try {
        const ruleConfig = single ? { stage1RoundIds } : { stage1RoundIds, stage2RoundIds }
        await saveDragonScoringRule({ eventId: this.event.id, scoringType: single ? 'single_stage_rank' : 'two_stage_sum', ruleConfig })
        this.configured = true
        this.$message.success('已保存，汇总已更新')
        await this.loadRows()
      } catch (e) {
        this.$message.error(e.message || '保存失败')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
<style scoped>
.results-center{padding:28px;background:#fff;border-radius:16px}
.results-center header{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;margin-bottom:16px}
.results-center h2{margin:8px 0;font-size:26px;color:#132c32}
.results-center p{color:#64748b;line-height:1.7}
.eyebrow{letter-spacing:.12em;font-size:11px;color:#187a78;font-weight:700}
.rule-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:0 20px;align-items:end}
.stage-add{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.stage-add .ant-tag{margin:0}
@media(max-width:800px){.results-center{padding:16px}.rule-grid{display:block}}
</style>
